import moment from 'moment';

/**
 * Projection Analytics Engine
 * Provides utilization projections, ROI analysis, and what-if scenarios
 * for material utilization and revenue optimization
 */

// Projection scenarios
export const PROJECTION_SCENARIOS = {
  CONSERVATIVE: 'conservative',
  MODERATE: 'moderate',
  AGGRESSIVE: 'aggressive',
  CUSTOM: 'custom'
};

// Projection time horizons
export const PROJECTION_HORIZONS = {
  QUARTERLY: 'quarterly',
  SEMI_ANNUAL: 'semi_annual',
  ANNUAL: 'annual',
  CUSTOM: 'custom'
};

// Market conditions
export const MARKET_CONDITIONS = {
  RECESSION: 'recession',
  STABLE: 'stable',
  GROWTH: 'growth',
  BOOM: 'boom'
};

/**
 * Material Utilization Projection Analytics
 * Calculates revenue projections based on utilization changes
 */
export class MaterialUtilizationProjections {
  constructor(currentData, historicalData = null) {
    this.currentData = currentData;
    this.historicalData = historicalData;
    this.baselineMetrics = this.calculateBaselineMetrics();
  }

  /**
   * Calculate baseline metrics from current data
   * @returns {Object} Baseline metrics
   */
  calculateBaselineMetrics() {
    const { utilization, hybridMaterialFinancial } = this.currentData;
    
    return {
      currentUtilizationRate: utilization.metrics.utilizationRate,
      currentAvailabilityRate: utilization.metrics.availabilityRate,
      currentTotalRevenue: hybridMaterialFinancial.financialMetrics.totalRevenue,
      currentTotalInventory: utilization.metrics.totalInventoryItems,
      currentDamageRate: utilization.metrics.damageRate,
      currentTurnoverRate: utilization.metrics.turnoverRate,
      materialBreakdown: utilization.materialBreakdown,
      topMaterials: hybridMaterialFinancial.financialMetrics.topMaterials
    };
  }

  /**
   * Generate utilization impact projections
   * @param {Array} utilizationChanges - Array of utilization change percentages
   * @param {string} timeHorizon - Projection time horizon
   * @param {string} marketCondition - Market condition assumption
   * @returns {Object} Utilization projections
   */
  generateUtilizationProjections(utilizationChanges = [10, 20, 30, -10, -20], timeHorizon = PROJECTION_HORIZONS.ANNUAL, marketCondition = MARKET_CONDITIONS.STABLE) {
    const projections = [];
    const baseline = this.baselineMetrics;

    utilizationChanges.forEach(changePercent => {
      const projection = this.calculateUtilizationScenario(changePercent, timeHorizon, marketCondition);
      projections.push(projection);
    });

    return {
      baseline: {
        utilizationRate: baseline.currentUtilizationRate,
        revenue: baseline.currentTotalRevenue,
        availabilityRate: baseline.currentAvailabilityRate,
        scenario: 'Current State'
      },
      projections,
      recommendations: this.generateUtilizationRecommendations(projections),
      optimalScenario: this.findOptimalScenario(projections),
      riskAnalysis: this.calculateRiskAnalysis(projections)
    };
  }

  /**
   * Calculate specific utilization scenario
   * @param {number} changePercent - Utilization change percentage
   * @param {string} timeHorizon - Time horizon
   * @param {string} marketCondition - Market condition
   * @returns {Object} Scenario projection
   */
  calculateUtilizationScenario(changePercent, timeHorizon, marketCondition) {
    const baseline = this.baselineMetrics;
    const newUtilizationRate = Math.max(0, Math.min(100, baseline.currentUtilizationRate + changePercent));
    
    // Calculate capacity constraints
    const maxUtilizationRate = this.calculateMaxUtilizationRate();
    const constrainedUtilizationRate = Math.min(newUtilizationRate, maxUtilizationRate);
    
    // Calculate revenue impact
    const revenueMultiplier = this.calculateRevenueMultiplier(changePercent, marketCondition);
    const projectedRevenue = baseline.currentTotalRevenue * revenueMultiplier;
    
    // Calculate additional metrics
    const availabilityImpact = this.calculateAvailabilityImpact(changePercent);
    const newAvailabilityRate = Math.max(0, baseline.currentAvailabilityRate + availabilityImpact);
    
    // Calculate operational costs
    const operationalCosts = this.calculateOperationalCosts(changePercent, timeHorizon);
    
    // Calculate net revenue
    const netRevenue = projectedRevenue - operationalCosts.additional;
    
    // Calculate ROI
    const roi = operationalCosts.investment > 0 
      ? ((netRevenue - baseline.currentTotalRevenue) / operationalCosts.investment) * 100 
      : 0;

    return {
      scenario: changePercent > 0 ? `+${changePercent}% Utilization` : `${changePercent}% Utilization`,
      changePercent,
      utilizationRate: constrainedUtilizationRate,
      availabilityRate: newAvailabilityRate,
      projectedRevenue,
      netRevenue,
      revenueIncrease: projectedRevenue - baseline.currentTotalRevenue,
      revenueIncreasePercent: ((projectedRevenue - baseline.currentTotalRevenue) / baseline.currentTotalRevenue) * 100,
      operationalCosts,
      roi,
      timeHorizon,
      marketCondition,
      feasibility: this.calculateFeasibility(changePercent, constrainedUtilizationRate, newAvailabilityRate),
      materialImpact: this.calculateMaterialSpecificImpact(changePercent),
      constraints: this.identifyConstraints(changePercent, constrainedUtilizationRate)
    };
  }

  /**
   * Calculate maximum achievable utilization rate
   * @returns {number} Maximum utilization rate
   */
  calculateMaxUtilizationRate() {
    const baseline = this.baselineMetrics;
    
    // Consider damage rate and maintenance requirements
    const damageBuffer = baseline.currentDamageRate * 1.5; // Buffer for damage
    const maintenanceBuffer = 5; // 5% buffer for maintenance
    const operationalBuffer = 10; // 10% buffer for operations
    
    return Math.max(70, 100 - damageBuffer - maintenanceBuffer - operationalBuffer);
  }

  /**
   * Calculate revenue multiplier based on utilization change and market conditions
   * @param {number} changePercent - Utilization change percentage
   * @param {string} marketCondition - Market condition
   * @returns {number} Revenue multiplier
   */
  calculateRevenueMultiplier(changePercent, marketCondition) {
    // Base multiplier from utilization change
    let baseMultiplier = 1 + (changePercent / 100);
    
    // Market condition adjustments
    const marketMultipliers = {
      [MARKET_CONDITIONS.RECESSION]: 0.85,
      [MARKET_CONDITIONS.STABLE]: 1.0,
      [MARKET_CONDITIONS.GROWTH]: 1.15,
      [MARKET_CONDITIONS.BOOM]: 1.3
    };
    
    const marketMultiplier = marketMultipliers[marketCondition] || 1.0;
    
    // Diminishing returns for high utilization increases
    if (changePercent > 20) {
      const diminishingFactor = 1 - ((changePercent - 20) * 0.01);
      baseMultiplier *= diminishingFactor;
    }
    
    return baseMultiplier * marketMultiplier;
  }

  /**
   * Calculate availability impact from utilization changes
   * @param {number} changePercent - Utilization change percentage
   * @returns {number} Availability rate change
   */
  calculateAvailabilityImpact(changePercent) {
    // Higher utilization typically reduces availability
    if (changePercent > 0) {
      return -changePercent * 0.6; // 60% of utilization increase reduces availability
    } else {
      return Math.abs(changePercent) * 0.8; // 80% of utilization decrease increases availability
    }
  }

  /**
   * Calculate operational costs for utilization changes
   * @param {number} changePercent - Utilization change percentage
   * @param {string} timeHorizon - Time horizon
   * @returns {Object} Operational costs breakdown
   */
  calculateOperationalCosts(changePercent, timeHorizon) {
    const baseline = this.baselineMetrics;
    const timeMultiplier = this.getTimeMultiplier(timeHorizon);
    
    let additionalCosts = 0;
    let investmentRequired = 0;
    
    if (changePercent > 0) {
      // Increased utilization costs
      const maintenanceCostIncrease = baseline.currentTotalRevenue * 0.05 * (changePercent / 100);
      const operationalCostIncrease = baseline.currentTotalRevenue * 0.03 * (changePercent / 100);
      
      additionalCosts = (maintenanceCostIncrease + operationalCostIncrease) * timeMultiplier;
      
      // Investment for high utilization increases
      if (changePercent > 15) {
        const inventoryExpansionCost = this.calculateInventoryExpansionCost(changePercent);
        investmentRequired = inventoryExpansionCost;
      }
    } else {
      // Reduced utilization might save some costs
      const costSavings = Math.abs(baseline.currentTotalRevenue * 0.02 * (changePercent / 100));
      additionalCosts = -costSavings * timeMultiplier;
    }
    
    return {
      additional: additionalCosts,
      investment: investmentRequired,
      breakdown: {
        maintenance: additionalCosts * 0.6,
        operations: additionalCosts * 0.4,
        inventoryExpansion: investmentRequired
      }
    };
  }

  /**
   * Calculate inventory expansion cost
   * @param {number} changePercent - Utilization change percentage
   * @returns {number} Expansion cost
   */
  calculateInventoryExpansionCost(changePercent) {
    const baseline = this.baselineMetrics;
    
    // Estimate cost per inventory item (average across all materials)
    const avgCostPerItem = 1500; // Based on scaffold material costs
    
    // Calculate additional inventory needed
    const additionalItemsNeeded = Math.ceil(baseline.currentTotalInventory * (changePercent / 100) * 0.5);
    
    return additionalItemsNeeded * avgCostPerItem;
  }

  /**
   * Get time multiplier for projections
   * @param {string} timeHorizon - Time horizon
   * @returns {number} Time multiplier
   */
  getTimeMultiplier(timeHorizon) {
    const multipliers = {
      [PROJECTION_HORIZONS.QUARTERLY]: 0.25,
      [PROJECTION_HORIZONS.SEMI_ANNUAL]: 0.5,
      [PROJECTION_HORIZONS.ANNUAL]: 1.0
    };
    
    return multipliers[timeHorizon] || 1.0;
  }

  /**
   * Calculate feasibility score for scenario
   * @param {number} changePercent - Utilization change percentage
   * @param {number} newUtilizationRate - New utilization rate
   * @param {number} newAvailabilityRate - New availability rate
   * @returns {Object} Feasibility assessment
   */
  calculateFeasibility(changePercent, newUtilizationRate, newAvailabilityRate) {
    let score = 100;
    const issues = [];
    
    // Check utilization constraints
    if (newUtilizationRate > 85) {
      score -= 20;
      issues.push('High utilization may strain operations');
    }
    
    // Check availability constraints
    if (newAvailabilityRate < 20) {
      score -= 30;
      issues.push('Low availability may impact customer satisfaction');
    }
    
    // Check change magnitude
    if (Math.abs(changePercent) > 25) {
      score -= 15;
      issues.push('Large changes may be difficult to implement quickly');
    }
    
    // Check balance
    if (newUtilizationRate + newAvailabilityRate < 70) {
      score -= 25;
      issues.push('Poor utilization-availability balance');
    }
    
    return {
      score: Math.max(0, score),
      level: score > 80 ? 'High' : score > 60 ? 'Medium' : 'Low',
      issues
    };
  }

  /**
   * Calculate material-specific impact
   * @param {number} changePercent - Utilization change percentage
   * @returns {Object} Material-specific projections
   */
  calculateMaterialSpecificImpact(changePercent) {
    const baseline = this.baselineMetrics;
    const materialImpact = {};
    
    Object.entries(baseline.materialBreakdown).forEach(([materialType, data]) => {
      const currentUtilization = data.total > 0 ? (data.deployed / data.total) * 100 : 0;
      const newUtilization = Math.max(0, Math.min(100, currentUtilization + changePercent));
      
      // Find revenue data for this material
      const revenueData = baseline.topMaterials.find(m => 
        m.materialType === materialType || m.materialType.includes(materialType)
      );
      
      const currentRevenue = revenueData ? revenueData.revenue : 0;
      const projectedRevenue = currentRevenue * (1 + changePercent / 100);
      
      materialImpact[materialType] = {
        currentUtilization,
        projectedUtilization: newUtilization,
        currentRevenue,
        projectedRevenue,
        revenueIncrease: projectedRevenue - currentRevenue,
        feasible: newUtilization <= 90 && data.available > 0
      };
    });
    
    return materialImpact;
  }

  /**
   * Identify constraints for scenario
   * @param {number} changePercent - Utilization change percentage
   * @param {number} newUtilizationRate - New utilization rate
   * @returns {Array} List of constraints
   */
  identifyConstraints(changePercent, newUtilizationRate) {
    const constraints = [];
    const baseline = this.baselineMetrics;
    
    if (changePercent > 0) {
      // Increasing utilization constraints
      if (baseline.currentAvailabilityRate < 30) {
        constraints.push({
          type: 'inventory',
          message: 'Limited available inventory may restrict utilization increases',
          severity: 'high'
        });
      }
      
      if (baseline.currentDamageRate > 15) {
        constraints.push({
          type: 'maintenance',
          message: 'High damage rate may limit sustainable utilization increases',
          severity: 'medium'
        });
      }
      
      if (newUtilizationRate > 85) {
        constraints.push({
          type: 'operational',
          message: 'Very high utilization may impact service quality',
          severity: 'high'
        });
      }
    } else {
      // Decreasing utilization constraints
      constraints.push({
        type: 'revenue',
        message: 'Reduced utilization will directly impact revenue',
        severity: 'medium'
      });
    }
    
    return constraints;
  }

  /**
   * Generate utilization recommendations
   * @param {Array} projections - All scenario projections
   * @returns {Array} Recommendations
   */
  generateUtilizationRecommendations(projections) {
    const recommendations = [];
    
    // Find best ROI scenario
    const bestROI = projections.reduce((best, current) => 
      current.roi > best.roi ? current : best
    );
    
    if (bestROI.roi > 20) {
      recommendations.push({
        title: 'Optimize for Best ROI',
        description: `${bestROI.scenario} offers the best ROI at ${bestROI.roi.toFixed(1)}%`,
        priority: 'high',
        impact: 'High',
        timeframe: '2-3 months'
      });
    }
    
    // Check for high-feasibility scenarios
    const highFeasibility = projections.filter(p => p.feasibility.score > 80);
    if (highFeasibility.length > 0) {
      const bestFeasible = highFeasibility.reduce((best, current) => 
        current.revenueIncrease > best.revenueIncrease ? current : best
      );
      
      recommendations.push({
        title: 'Implement High-Feasibility Scenario',
        description: `${bestFeasible.scenario} is highly feasible and could increase revenue by $${bestFeasible.revenueIncrease.toLocaleString()}`,
        priority: 'medium',
        impact: 'Medium',
        timeframe: '1-2 months'
      });
    }
    
    // Check for inventory expansion needs
    const highUtilizationScenarios = projections.filter(p => p.changePercent > 15);
    if (highUtilizationScenarios.length > 0) {
      recommendations.push({
        title: 'Consider Inventory Expansion',
        description: 'High utilization scenarios may require additional inventory investment',
        priority: 'medium',
        impact: 'High',
        timeframe: '3-6 months'
      });
    }
    
    return recommendations;
  }

  /**
   * Find optimal scenario
   * @param {Array} projections - All scenario projections
   * @returns {Object} Optimal scenario
   */
  findOptimalScenario(projections) {
    // Score each scenario based on multiple factors
    const scoredProjections = projections.map(projection => {
      let score = 0;
      
      // Revenue increase weight (40%)
      score += (projection.revenueIncreasePercent / 100) * 40;
      
      // ROI weight (30%)
      score += (projection.roi / 100) * 30;
      
      // Feasibility weight (20%)
      score += (projection.feasibility.score / 100) * 20;
      
      // Risk adjustment (10%)
      const riskPenalty = Math.abs(projection.changePercent) > 20 ? 10 : 0;
      score -= riskPenalty * 0.1;
      
      return {
        ...projection,
        optimizationScore: Math.max(0, score)
      };
    });
    
    return scoredProjections.reduce((best, current) => 
      current.optimizationScore > best.optimizationScore ? current : best
    );
  }

  /**
   * Calculate risk analysis
   * @param {Array} projections - All scenario projections
   * @returns {Object} Risk analysis
   */
  calculateRiskAnalysis(projections) {
    const risks = {
      low: projections.filter(p => Math.abs(p.changePercent) <= 10),
      medium: projections.filter(p => Math.abs(p.changePercent) > 10 && Math.abs(p.changePercent) <= 20),
      high: projections.filter(p => Math.abs(p.changePercent) > 20)
    };
    
    return {
      riskDistribution: {
        low: risks.low.length,
        medium: risks.medium.length,
        high: risks.high.length
      },
      recommendedRiskLevel: 'medium',
      riskFactors: [
        'Market condition changes',
        'Seasonal demand variations',
        'Equipment maintenance requirements',
        'Competition and pricing pressure'
      ],
      mitigation: [
        'Gradual implementation of utilization changes',
        'Regular monitoring and adjustment',
        'Maintain adequate inventory buffers',
        'Diversify material portfolio'
      ]
    };
  }
}

/**
 * ROI Analysis for Inventory Investment
 * Calculates return on investment for different inventory scenarios
 */
export class InventoryROIAnalysis {
  constructor(currentData, investmentScenarios = []) {
    this.currentData = currentData;
    this.investmentScenarios = investmentScenarios;
  }

  /**
   * Generate ROI analysis for inventory investments
   * @param {Array} investmentAmounts - Investment amounts to analyze
   * @param {string} timeHorizon - Analysis time horizon
   * @returns {Object} ROI analysis
   */
  generateROIAnalysis(investmentAmounts = [50000, 100000, 200000, 500000], timeHorizon = PROJECTION_HORIZONS.ANNUAL) {
    const analyses = [];
    
    investmentAmounts.forEach(amount => {
      const analysis = this.calculateInvestmentROI(amount, timeHorizon);
      analyses.push(analysis);
    });
    
    return {
      analyses,
      recommendations: this.generateInvestmentRecommendations(analyses),
      breakEvenAnalysis: this.calculateBreakEvenPoints(analyses),
      riskAssessment: this.assessInvestmentRisks(analyses)
    };
  }

  /**
   * Calculate ROI for specific investment amount
   * @param {number} investmentAmount - Investment amount
   * @param {string} timeHorizon - Time horizon
   * @returns {Object} Investment analysis
   */
  calculateInvestmentROI(investmentAmount, timeHorizon) {
    const baseline = this.currentData.hybridMaterialFinancial.financialMetrics;
    const utilization = this.currentData.utilization.metrics;
    
    // Estimate additional inventory capacity
    const avgCostPerItem = 1500;
    const additionalItems = Math.floor(investmentAmount / avgCostPerItem);
    
    // Calculate capacity increase
    const capacityIncrease = (additionalItems / utilization.totalInventoryItems) * 100;
    
    // Estimate revenue increase
    const revenueIncrease = this.estimateRevenueFromCapacity(additionalItems, timeHorizon);
    
    // Calculate operational costs
    const operationalCosts = this.calculateOperationalCosts(additionalItems, timeHorizon);
    
    // Calculate net return
    const netReturn = revenueIncrease - operationalCosts;
    const roi = (netReturn / investmentAmount) * 100;
    
    // Calculate payback period
    const monthlyReturn = netReturn / 12;
    const paybackMonths = monthlyReturn > 0 ? investmentAmount / monthlyReturn : Infinity;
    
    return {
      investmentAmount,
      additionalItems,
      capacityIncrease,
      revenueIncrease,
      operationalCosts,
      netReturn,
      roi,
      paybackMonths,
      timeHorizon,
      feasibility: this.assessInvestmentFeasibility(investmentAmount, roi, paybackMonths),
      materialMix: this.recommendMaterialMix(investmentAmount)
    };
  }

  /**
   * Estimate revenue from additional capacity
   * @param {number} additionalItems - Additional inventory items
   * @param {string} timeHorizon - Time horizon
   * @returns {number} Estimated revenue increase
   */
  estimateRevenueFromCapacity(additionalItems, timeHorizon) {
    const baseline = this.currentData.hybridMaterialFinancial.financialMetrics;
    const utilization = this.currentData.utilization.metrics;
    
    // Calculate average revenue per item
    const avgRevenuePerItem = baseline.totalRevenue / utilization.totalInventoryItems;
    
    // Apply utilization rate and time multiplier
    const timeMultiplier = this.getTimeMultiplier(timeHorizon);
    const utilizationFactor = utilization.utilizationRate / 100;
    
    return additionalItems * avgRevenuePerItem * utilizationFactor * timeMultiplier;
  }

  /**
   * Calculate operational costs for additional inventory
   * @param {number} additionalItems - Additional inventory items
   * @param {string} timeHorizon - Time horizon
   * @returns {number} Operational costs
   */
  calculateOperationalCosts(additionalItems, timeHorizon) {
    const timeMultiplier = this.getTimeMultiplier(timeHorizon);
    
    // Storage costs
    const storageCostPerItem = 50; // Annual storage cost per item
    const storageCosts = additionalItems * storageCostPerItem * timeMultiplier;
    
    // Maintenance costs
    const maintenanceCostPerItem = 100; // Annual maintenance cost per item
    const maintenanceCosts = additionalItems * maintenanceCostPerItem * timeMultiplier;
    
    // Insurance and other costs
    const otherCosts = (storageCosts + maintenanceCosts) * 0.2;
    
    return storageCosts + maintenanceCosts + otherCosts;
  }

  /**
   * Get time multiplier for ROI calculations
   * @param {string} timeHorizon - Time horizon
   * @returns {number} Time multiplier
   */
  getTimeMultiplier(timeHorizon) {
    const multipliers = {
      [PROJECTION_HORIZONS.QUARTERLY]: 0.25,
      [PROJECTION_HORIZONS.SEMI_ANNUAL]: 0.5,
      [PROJECTION_HORIZONS.ANNUAL]: 1.0
    };
    
    return multipliers[timeHorizon] || 1.0;
  }

  /**
   * Assess investment feasibility
   * @param {number} investmentAmount - Investment amount
   * @param {number} roi - ROI percentage
   * @param {number} paybackMonths - Payback period in months
   * @returns {Object} Feasibility assessment
   */
  assessInvestmentFeasibility(investmentAmount, roi, paybackMonths) {
    let score = 100;
    const issues = [];
    
    // ROI assessment
    if (roi < 15) {
      score -= 30;
      issues.push('Low ROI may not justify investment');
    } else if (roi > 50) {
      score += 10;
    }
    
    // Payback period assessment
    if (paybackMonths > 24) {
      score -= 25;
      issues.push('Long payback period increases risk');
    } else if (paybackMonths < 12) {
      score += 10;
    }
    
    // Investment size assessment
    if (investmentAmount > 300000) {
      score -= 15;
      issues.push('Large investment requires careful consideration');
    }
    
    return {
      score: Math.max(0, score),
      level: score > 80 ? 'High' : score > 60 ? 'Medium' : 'Low',
      issues
    };
  }

  /**
   * Recommend material mix for investment
   * @param {number} investmentAmount - Investment amount
   * @returns {Object} Material mix recommendation
   */
  recommendMaterialMix(investmentAmount) {
    const topMaterials = this.currentData.hybridMaterialFinancial.financialMetrics.topMaterials;
    const totalBudget = investmentAmount;
    
    // Allocate budget based on material performance
    const allocation = {};
    let remainingBudget = totalBudget;
    
    // Prioritize top-performing materials
    topMaterials.slice(0, 3).forEach((material, index) => {
      const allocationPercent = index === 0 ? 0.4 : index === 1 ? 0.3 : 0.2;
      const materialBudget = totalBudget * allocationPercent;
      
      allocation[material.materialType] = {
        budget: materialBudget,
        estimatedItems: Math.floor(materialBudget / this.getEstimatedMaterialCost(material.materialType)),
        reason: `High performance material with ${material.revenuePerUnit.toFixed(0)} revenue per unit`
      };
      
      remainingBudget -= materialBudget;
    });
    
    // Allocate remaining budget to diversification
    if (remainingBudget > 0) {
      allocation['diversification'] = {
        budget: remainingBudget,
        estimatedItems: Math.floor(remainingBudget / 1500),
        reason: 'Portfolio diversification and risk mitigation'
      };
    }
    
    return allocation;
  }

  /**
   * Get estimated material cost
   * @param {string} materialType - Material type
   * @returns {number} Estimated cost
   */
  getEstimatedMaterialCost(materialType) {
    const costs = {
      'SW_FRAME': 120,
      'DW_FRAME': 180,
      'SW_PLATFORM': 90,
      'DW_PLATFORM': 135,
      'HORIZONTAL_BRACE': 45,
      'DIAGONAL_BRACE': 45,
      'OUTRIGGER_2M': 225,
      'OUTRIGGER_3M': 270,
      'OUTRIGGER_4M': 315,
      'OUTRIGGER_5M': 360
    };
    
    return costs[materialType] || 1500; // Default cost
  }

  /**
   * Generate investment recommendations
   * @param {Array} analyses - Investment analyses
   * @returns {Array} Recommendations
   */
  generateInvestmentRecommendations(analyses) {
    const recommendations = [];
    
    // Find best ROI investment
    const bestROI = analyses.reduce((best, current) => 
      current.roi > best.roi ? current : best
    );
    
    if (bestROI.roi > 25) {
      recommendations.push({
        title: 'High ROI Investment Opportunity',
        description: `$${bestROI.investmentAmount.toLocaleString()} investment offers ${bestROI.roi.toFixed(1)}% ROI`,
        priority: 'high',
        impact: 'High',
        timeframe: `${bestROI.paybackMonths.toFixed(0)} months payback`
      });
    }
    
    // Find most feasible investment
    const mostFeasible = analyses.reduce((best, current) => 
      current.feasibility.score > best.feasibility.score ? current : best
    );
    
    if (mostFeasible.feasibility.score > 80) {
      recommendations.push({
        title: 'Low-Risk Investment Option',
        description: `$${mostFeasible.investmentAmount.toLocaleString()} investment has high feasibility score`,
        priority: 'medium',
        impact: 'Medium',
        timeframe: `${mostFeasible.paybackMonths.toFixed(0)} months payback`
      });
    }
    
    return recommendations;
  }

  /**
   * Calculate break-even points for investments
   * @param {Array} analyses - Investment analyses
   * @returns {Object} Break-even analysis
   */
  calculateBreakEvenPoints(analyses) {
    return analyses.map(analysis => ({
      investmentAmount: analysis.investmentAmount,
      breakEvenMonths: analysis.paybackMonths,
      breakEvenRevenue: analysis.investmentAmount + analysis.operationalCosts,
      monthlyRevenueRequired: analysis.netReturn / 12
    }));
  }

  /**
   * Assess investment risks
   * @param {Array} analyses - Investment analyses
   * @returns {Object} Risk assessment
   */
  assessInvestmentRisks(analyses) {
    const riskFactors = [
      'Market demand fluctuations',
      'Equipment depreciation',
      'Maintenance cost increases',
      'Competition and pricing pressure',
      'Economic downturns'
    ];

    const riskMitigation = [
      'Diversify material portfolio',
      'Maintain adequate cash reserves',
      'Regular market analysis',
      'Flexible pricing strategies',
      'Strong maintenance programs'
    ];

    return {
      riskFactors,
      riskMitigation,
      overallRisk: 'Medium',
      recommendation: 'Proceed with moderate investments while monitoring market conditions'
    };
  }
}
